import { FC } from "react";
import { Stack } from "@altimateai/lego";
import { Button, Skeleton, Tooltip, Typography } from "@altimateai/lego";
import { useAgentChat } from "@altimateai/ui-components/chatbotV2";
import { getStarterQuestions } from "@/helpers/apis";
import { useQuery } from "@tanstack/react-query";
import { isFreeStack } from "@/helpers/tenant";
import { useAppState } from "@/modules/app/useAppContext";

interface PlaygroundHelloMessageFooterProps {
  onOpenPromptLibrary?: () => void;
}

export const PlaygroundHelloMessageFooter: FC<PlaygroundHelloMessageFooterProps> = ({
  onOpenPromptLibrary,
}) => {
  const { enablePromptLibrary, isDatabricksTenant } = useAppState();
  const { updateContextAndSendMessage } = useAgentChat();
  const integrationType = isDatabricksTenant ? "databricks" : isFreeStack() ? "dbt" : "snowflake";

  const { data, isLoading } = useQuery({
    queryKey: ["starter-prompts", integrationType],
    queryFn: () =>
      getStarterQuestions({
        publicMax: 5,
        tenantMax: 0,
        integrationType,
      }),
  });

  const prompts = data?.prompts ?? [];

  const handleClick = async (question: string) => {
    await updateContextAndSendMessage(question, {});
  };

  const truncateText = (text: string, wordLimit: number) => {
    const words = text.split(" ");
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(" ") + "...";
    }
    return text;
  };

  return (
    <Stack direction="column" className="al-px-4" justify="center" align="center">
      {enablePromptLibrary && (
        <Typography size={"xs"}>
          Suggested Prompts:{" "}
          <Button onClick={onOpenPromptLibrary} className={"!al-p-0 !al-h-0"} variant={"link"}>
            View Prompt Library
          </Button>
        </Typography>
      )}
      <Stack wrap justify="center" align="center" aria-label="Questions" className="al-w-full">
        {isLoading ? (
          <Stack align={"center"}>
            <Skeleton className={"al-w-[256px] !al-bg-gray-200 al-h-6 !al-rounded-full"} />
            <Skeleton className={"al-w-[256px] !al-bg-gray-200 al-h-6 !al-rounded-full"} />
            <Skeleton className={"al-w-[256px] !al-bg-gray-200 al-h-6 !al-rounded-full"} />
            <Skeleton className={"al-w-[256px] !al-bg-gray-200 al-h-6 !al-rounded-full"} />
          </Stack>
        ) : (
          <>
            {prompts.map(question => (
              <Tooltip
                key={question.id}
                title={
                  <Stack className={"!al-max-w-[512px]"}>
                    <span>{question.prompt_text}</span>
                  </Stack>
                }
              >
                <Button
                  variant="outline"
                  onClick={async () => {
                    await handleClick(question.prompt_text);
                  }}
                  size="sm"
                  className="!al-rounded-full !al-font-normal"
                >
                  {truncateText(question.prompt_text, 6)}
                </Button>
              </Tooltip>
            ))}
          </>
        )}
      </Stack>
    </Stack>
  );
};
